import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import VoiceDictationInput from '../components/VoiceDictationInput';
import ESignaturePanel from '../components/ESignaturePanel';
import { useESignature } from '../hooks/useESignature';
import { FileText, Save, CheckCircle, ArrowLeft, User, Shield, Heart, DollarSign, Camera, FileCheck, Download, Printer } from 'lucide-react';
import { exportFormPdfFromDom } from '../utils/domFormPdf';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdmissionConsentForm = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const patientId = searchParams.get('patientId');
  const formId = searchParams.get('formId');
  
  const { getDocumentSignatures } = useESignature();
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [patients, setPatients] = useState([]);
  const [signatures, setSignatures] = useState({
    patient: null,
    responsiblePerson: null,
    representative: null
  });
  const [formData, setFormData] = useState({
    patientId: patientId || '',
    patientName: '',
    patientDOB: '',
    // Acknowledgments
    receivedRightsResponsibilities: false,
    consentForTreatment: false,
    releaseOfInformation: false,
    financialAgreement: false,
    assignmentOfBenefits: false,
    consentToFilm: false,
    // Advance Directives
    hasLivingWill: null,
    hasHealthCarePOA: null,
    poaName: '',
    poaPhone: '',
    hasDNR: null,
    // Signature IDs (stored after e-signature creation)
    patientSignatureId: null,
    responsiblePersonSignatureId: null,
    representativeSignatureId: null,
    responsiblePersonRelationship: '',
    patientUnableToSignReason: ''
  });

  useEffect(() => {
    fetchPatients();
    if (formId) {
      fetchForm();
      loadSignatures();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formId]);

  const loadSignatures = async () => {
    if (!formId) return;
    const result = await getDocumentSignatures(formId);
    if (result.success && result.signatures) {
      const sigMap = {};
      result.signatures.forEach(sig => {
        if (sig.signerTitle === 'patient') sigMap.patient = sig;
        if (sig.signerTitle === 'responsible_person') sigMap.responsiblePerson = sig;
        if (sig.signerTitle === 'representative') sigMap.representative = sig;
      });
      setSignatures(sigMap);
    }
  };

  const handleSignatureComplete = (signerType) => (signatureRecord) => {
    setSignatures(prev => ({ ...prev, [signerType]: signatureRecord }));
    setFormData(prev => ({
      ...prev,
      [`${signerType}SignatureId`]: signatureRecord.id
    }));
  };

  const fetchPatients = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/patients`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setPatients(data.patients);
        // If patientId is provided, set the patient name
        if (patientId) {
          const patient = data.patients.find(p => p.id === patientId);
          if (patient) {
            setFormData(prev => ({
              ...prev,
              patientName: `${patient.firstName} ${patient.lastName}`,
              patientDOB: patient.dateOfBirth
            }));
          }
        }
      }
    } catch (error) {
      console.error('Error fetching patients:', error);
    }
  };

  const fetchForm = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/forms/admission-consent/${formId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setFormData(data.form);
      }
    } catch (error) {
      console.error('Error fetching form:', error);
      toast.error('Failed to load form');
    } finally {
      setLoading(false);
    }
  };

  const handlePatientChange = (e) => {
    const selectedPatientId = e.target.value;
    const patient = patients.find(p => p.id === selectedPatientId);
    if (patient) {
      setFormData(prev => ({
        ...prev,
        patientId: selectedPatientId,
        patientName: `${patient.firstName} ${patient.lastName}`,
        patientDOB: patient.dateOfBirth
      }));
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!formData.patientId) {
      toast.error('Please select a patient');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const url = formId 
        ? `${API_URL}/api/forms/admission-consent/${formId}`
        : `${API_URL}/api/forms/admission-consent`;
      
      const response = await fetch(url, {
        method: formId ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();
      if (data.success) {
        toast.success(formId ? 'Form updated successfully' : 'Form created successfully');
        if (!formId && data.form) {
          navigate(`/dashboard/forms/admission-consent?formId=${data.form.id}`);
        }
      } else {
        toast.error('Failed to save form');
      }
    } catch (error) {
      console.error('Error saving form:', error);
      toast.error('Failed to save form');
    } finally {
      setSaving(false);
    }
  };

  const handleExportPDF = () => {
    const p = patients.find(x => x.id === formData.patientId) || {};
    exportFormPdfFromDom(document.getElementById('main-content'), 'Admission Consent',
      { ...p, name: formData.patientName || undefined, dateOfBirth: formData.patientDOB || p.dateOfBirth }, `admission-consent-${formData.patientName || 'form'}.pdf`);
  };

  const handlePrintPDF = handleExportPDF;

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#f39c12]"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button 
                variant="ghost" 
                onClick={() => navigate('/dashboard/patients/admission')}
                className="p-2"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                  <FileText className="w-6 h-6 text-[#f39c12]" />
                  Admission Consent Form
                </h1>
                <p className="text-gray-500 text-sm mt-1">IVORY HOME HEALTH CARE, INC.</p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button 
                variant="outline"
                onClick={handleExportPDF}
                data-testid="export-pdf-btn"
              >
                <Download className="w-4 h-4 mr-2" /> Export PDF
              </Button>
              <Button 
                variant="outline"
                onClick={handlePrintPDF}
                data-testid="print-pdf-btn"
              >
                <Printer className="w-4 h-4 mr-2" /> Print
              </Button>
              <Button 
                onClick={handleSave} 
                disabled={saving}
                className="bg-[#f39c12] hover:bg-[#e67e22] text-white"
              >
                {saving ? 'Saving...' : <><Save className="w-4 h-4 mr-2" /> Save Form</>}
              </Button>
            </div>
          </div>
        </div>

        {/* Patient Identification */}
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <User className="w-5 h-5 text-blue-600" />
            Patient Identification
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Patient Name *</Label>
              <select
                value={formData.patientId}
                onChange={handlePatientChange}
                className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#f39c12] focus:border-transparent"
              >
                <option value="">Select a patient</option>
                {patients.map(patient => (
                  <option key={patient.id} value={patient.id}>
                    {patient.firstName} {patient.lastName}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <Label>Date of Birth</Label>
              <Input
                type="date"
                value={formData.patientDOB || ''}
                onChange={(e) => handleInputChange('patientDOB', e.target.value)}
                className="mt-1"
              />
            </div>
          </div>
        </div>

        {/* Patient Rights and Responsibilities */}
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Shield className="w-5 h-5 text-green-600" />
            Patient Rights and Responsibilities
          </h2>
          <div className="bg-gray-50 p-4 rounded-lg mb-4 text-sm text-gray-700">
            <p>I acknowledge that I have been provided with a written copy of my rights and responsibilities as a patient. A Home Health representative has discussed them with me, and I understand them. The state hospice Care/Home Health hotline number, its purpose and hours of operation have been provided and explained to me. I acknowledge that I have chosen this agency to provide my Home Health Care without solicitation or coercion from the Home Health agency.</p>
          </div>
          <div className="flex items-center space-x-2">
            <Checkbox
              id="receivedRightsResponsibilities"
              checked={formData.receivedRightsResponsibilities}
              onCheckedChange={(checked) => handleInputChange('receivedRightsResponsibilities', checked)}
            />
            <Label htmlFor="receivedRightsResponsibilities" className="font-normal">
              I acknowledge receipt of Patient Rights and Responsibilities
            </Label>
          </div>
        </div>

        {/* Consent for Treatment */}
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Heart className="w-5 h-5 text-red-500" />
            Consent for Treatment
          </h2>
          <div className="bg-gray-50 p-4 rounded-lg mb-4 text-sm text-gray-700">
            <p>I hereby give my permission for authorized personnel of your Home Health to perform all necessary assessments, procedures and treatments as prescribed by my physician for the delivery of Home Health Care. I understand the following Home Health Care and services may be provided to me during the course of illness: physician, nursing, social work, therapy services, counseling services (bereavement, spiritual, dietary), Home Health aide/homemaker, volunteers, durable medical equipment, pharmaceuticals, medical supplies, respite Care, short-term inpatient Care and continuous Care.</p>
          </div>
          <div className="flex items-center space-x-2">
            <Checkbox
              id="consentForTreatment"
              checked={formData.consentForTreatment}
              onCheckedChange={(checked) => handleInputChange('consentForTreatment', checked)}
            />
            <Label htmlFor="consentForTreatment" className="font-normal">
              I consent to treatment as described above
            </Label>
          </div>
        </div>

        {/* Release of Information */}
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <FileCheck className="w-5 h-5 text-purple-600" />
            Release of Information
          </h2>
          <div className="bg-gray-50 p-4 rounded-lg mb-4 text-sm text-gray-700">
            <p>I acknowledge receipt of the Notice of Privacy Practices and was given an opportunity to ask questions and voice concerns. I authorize the release of all medical records from any facility, physician or other professionals to the agency for the purpose of coordinating my healthcare. This authorization includes any record of diagnosis or treatment for HIV, AIDS, mental health, and drug or alcohol treatment.</p>
          </div>
          <div className="flex items-center space-x-2">
            <Checkbox
              id="releaseOfInformation"
              checked={formData.releaseOfInformation}
              onCheckedChange={(checked) => handleInputChange('releaseOfInformation', checked)}
            />
            <Label htmlFor="releaseOfInformation" className="font-normal">
              I authorize release of my medical information as described above
            </Label>
          </div>
        </div>

        {/* Financial Agreement */}
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-green-600" />
            Financial Agreement & Assignment of Benefits
          </h2>
          <div className="bg-gray-50 p-4 rounded-lg mb-4 text-sm text-gray-700">
            <p>I understand that it is my responsibility to pay my bill. I also understand that a representative of the agency will assist me in understanding what my personal costs will be for all home care services provided. If I have third party insurance coverage, I request that payments of authorized benefits from insurance be made directly on my behalf to the Home Health Agency providing my care.</p>
          </div>
          <div className="space-y-3">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="financialAgreement"
                checked={formData.financialAgreement}
                onCheckedChange={(checked) => handleInputChange('financialAgreement', checked)}
              />
              <Label htmlFor="financialAgreement" className="font-normal">
                I agree to the financial terms described above
              </Label>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="assignmentOfBenefits"
                checked={formData.assignmentOfBenefits}
                onCheckedChange={(checked) => handleInputChange('assignmentOfBenefits', checked)}
              />
              <Label htmlFor="assignmentOfBenefits" className="font-normal">
                I authorize assignment of my insurance benefits to Ivory Home Health Care
              </Label>
            </div>
          </div>
        </div>

        {/* Consent to Film or Record */}
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Camera className="w-5 h-5 text-blue-500" />
            Consent to Film or Record
          </h2>
          <div className="bg-gray-50 p-4 rounded-lg mb-4 text-sm text-gray-700">
            <p>I hereby consent for the agency to record or film my Care, treatment and services and allow the agency to use the photographs/recordings for their internal use, for documenting my medical condition or for insurance providers to document my condition for payment purposes.</p>
          </div>
          <div className="flex items-center space-x-2">
            <Checkbox
              id="consentToFilm"
              checked={formData.consentToFilm}
              onCheckedChange={(checked) => handleInputChange('consentToFilm', checked)}
            />
            <Label htmlFor="consentToFilm" className="font-normal">
              I consent to filming/recording for medical documentation purposes
            </Label>
          </div>
        </div>

        {/* Advance Directives */}
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <FileText className="w-5 h-5 text-indigo-600" />
            Advance Directives
          </h2>
          <div className="bg-gray-50 p-4 rounded-lg mb-4 text-sm text-gray-700">
            <p>I have been made aware of my right to make health Care decisions for myself and that I may express my wishes in a document called an Advance Directive so that my wishes may be known when I am unable to speak for myself.</p>
          </div>
          <div className="space-y-4">
            {/* Living Will */}
            <div className="border-b pb-4">
              <Label className="text-sm font-medium">I have made a Living Will</Label>
              <div className="flex gap-4 mt-2">
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="hasLivingWill"
                    checked={formData.hasLivingWill === true}
                    onChange={() => handleInputChange('hasLivingWill', true)}
                    className="w-4 h-4 text-[#f39c12]"
                  />
                  Yes
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="hasLivingWill"
                    checked={formData.hasLivingWill === false}
                    onChange={() => handleInputChange('hasLivingWill', false)}
                    className="w-4 h-4 text-[#f39c12]"
                  />
                  No
                </label>
              </div>
              {formData.hasLivingWill === true && (
                <p className="text-sm text-amber-600 mt-2">Please provide a copy to the agency.</p>
              )}
            </div>

            {/* Health Care Power of Attorney */}
            <div className="border-b pb-4">
              <Label className="text-sm font-medium">I have Health Care Power of Attorney</Label>
              <div className="flex gap-4 mt-2">
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="hasHealthCarePOA"
                    checked={formData.hasHealthCarePOA === true}
                    onChange={() => handleInputChange('hasHealthCarePOA', true)}
                    className="w-4 h-4 text-[#f39c12]"
                  />
                  Yes
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="hasHealthCarePOA"
                    checked={formData.hasHealthCarePOA === false}
                    onChange={() => handleInputChange('hasHealthCarePOA', false)}
                    className="w-4 h-4 text-[#f39c12]"
                  />
                  No
                </label>
              </div>
              {formData.hasHealthCarePOA === true && (
                <div className="grid grid-cols-2 gap-4 mt-3">
                  <div>
                    <Label className="text-sm">POA Representative Name</Label>
                    <Input
                      value={formData.poaName || ''}
                      onChange={(e) => handleInputChange('poaName', e.target.value)}
                      placeholder="Enter name"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-sm">Phone</Label>
                    <Input
                      value={formData.poaPhone || ''}
                      onChange={(e) => handleInputChange('poaPhone', e.target.value)}
                      placeholder="Enter phone"
                      className="mt-1"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* DNR */}
            <div>
              <Label className="text-sm font-medium">I have a Do Not Resuscitate (DNR) Order / POLST</Label>
              <div className="flex gap-4 mt-2">
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="hasDNR"
                    checked={formData.hasDNR === true}
                    onChange={() => handleInputChange('hasDNR', true)}
                    className="w-4 h-4 text-[#f39c12]"
                  />
                  Yes
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="hasDNR"
                    checked={formData.hasDNR === false}
                    onChange={() => handleInputChange('hasDNR', false)}
                    className="w-4 h-4 text-[#f39c12]"
                  />
                  No
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Signatures - Comprehensive with Type/Voice Recording/Handwritten */}
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-green-600" />
            Acknowledgment and Signatures
          </h2>
          <div className="bg-gray-50 p-4 rounded-lg mb-4 text-sm text-gray-700">
            <p>By signing this consent, I acknowledge receipt of the orientation booklet and confirm my understanding and agreement with its contents. I understand a copy of this consent shall be as valid as the original and shall remain in effect until I am discharged from the agency.</p>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
            <p className="text-sm text-blue-700 flex items-center gap-2" data-no-pdf>
              <Shield className="w-4 h-4" />
              Electronic signatures are legally binding and securely stored. Choose your preferred method: Draw, Type, or Voice Recording.
            </p>
          </div>
          <div className="space-y-6">
            <ESignaturePanel 
              label="Patient Signature" 
              signerName={formData.patientName}
              signerTitle="patient"
              documentType="admission_consent"
              documentId={formId || ''}
              patientId={formData.patientId}
              existingSignatureId={signatures.patient?.id}
              onSignatureComplete={handleSignatureComplete('patient')}
            />
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <ESignaturePanel 
                label="Responsible Person/Legal Guardian Signature" 
                signerTitle="responsible_person"
                documentType="admission_consent"
                documentId={formId || ''}
                patientId={formData.patientId}
                existingSignatureId={signatures.responsiblePerson?.id}
                onSignatureComplete={handleSignatureComplete('responsiblePerson')}
              />
              <div>
                <Label>Relationship to Patient</Label>
                <Input
                  value={formData.responsiblePersonRelationship || ''}
                  onChange={(e) => handleInputChange('responsiblePersonRelationship', e.target.value)}
                  placeholder="e.g., Spouse, Child, Guardian"
                  className="mt-1"
                />
              </div>
            </div>
            <ESignaturePanel 
              label="Home Health Representative Signature" 
              signerTitle="representative"
              documentType="admission_consent"
              documentId={formId || ''}
              patientId={formData.patientId}
              existingSignatureId={signatures.representative?.id}
              onSignatureComplete={handleSignatureComplete('representative')}
            />
            <div>
              <Label>Patient unable to sign due to:</Label>
              <Input
                value={formData.patientUnableToSignReason || ''}
                onChange={(e) => handleInputChange('patientUnableToSignReason', e.target.value)}
                placeholder="If applicable, state reason"
                className="mt-1"
              />
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-between items-center mb-8">
          <Button
            variant="outline"
            onClick={() => navigate('/dashboard/patients/admission')}
          >
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-[#f39c12] hover:bg-[#e67e22] text-white"
          >
            {saving ? 'Saving...' : <><Save className="w-4 h-4 mr-2" /> Save Form</>}
          </Button>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default AdmissionConsentForm;
